import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/form/CustomButton";
import CustomButtonOval from "../../components/form/CustomButtonOval";
import CustomInput from "../../components/form/CustomInput";
import { infoToastCenter, successToast } from "../../helpers/Toast";
import { loginSuccess } from "../../store/auth/login/action";

const buildInitialForm = (user = {}) => ({
  first_name: user.first_name || "",
  last_name: user.last_name || "",
  email: user.email || "",
  phone: user.phone || user.mobile || "",
  role: user.role || "Sin rol asignado",
  company_name: user.company_name || "Sendero Logistics",
  bio:
    user.bio ||
    "Coordinación operativa, seguimiento de incidencias y administración diaria.",
});

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.Login.user);
  const auth = useSelector((state) => state.Login.auth);

  const [form, setForm] = useState(() => buildInitialForm(user));
  const [avatarPreview, setAvatarPreview] = useState(
    user.imageProfile || "/images/usrAnonimo.png"
  );

  useEffect(() => {
    setForm(buildInitialForm(user));
    setAvatarPreview(user.imageProfile || "/images/usrAnonimo.png");
  }, [user]);

  const fullName = useMemo(() => {
    return [form.first_name, form.last_name].filter(Boolean).join(" ") || "Usuario";
  }, [form.first_name, form.last_name]);

  const modulesCount = user?.models?.length || 0;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setAvatarPreview(reader.result);
        infoToastCenter("Foto cargada localmente. Guarda cambios para reflejarla en el perfil.");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleReset = () => {
    setForm(buildInitialForm(user));
    setAvatarPreview(user.imageProfile || "/images/usrAnonimo.png");
  };

  const handleSave = () => {
    const updatedUser = {
      ...user,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
      company_name: form.company_name,
      role: form.role,
      bio: form.bio,
      imageProfile: avatarPreview,
    };

    const payload = {
      user: updatedUser,
      token: auth?.token || "",
      isAuth: auth?.isAuth || false,
    };

    localStorage.setItem("authUser", JSON.stringify(payload));
    dispatch(loginSuccess(payload));
    successToast("Perfil actualizado localmente.");
  };

  return (
    <div className="min-h-full bg-cs-bg p-6">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-cs-dark">Perfil del usuario</h1>
          <p className="mt-1 text-sm text-cs-muted">
            Vista básica para consultar tu información y ajustar datos visibles del perfil.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <CustomButtonOval
            onClick={handleReset}
            className="border-cs-border bg-cs-surface text-cs-dark hover:bg-cs-bg-alt"
          >
            Restablecer
          </CustomButtonOval>
          <CustomButton onClick={handleSave} className="mb-0 mr-0 px-5 py-2.5">
            Guardar cambios
          </CustomButton>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px,minmax(0,1fr)]">
        <section className="overflow-hidden rounded-3xl border border-cs-border bg-white shadow-sm">
          <div className="bg-gradient-to-br from-cs-primary-soft via-white to-white p-6">
            <div className="mx-auto mb-4 h-28 w-28 overflow-hidden rounded-full border-4 border-white shadow-lg">
              <img
                src={avatarPreview}
                alt={fullName}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="text-center">
              <h2 className="text-xl font-semibold text-cs-dark">{fullName}</h2>
              <p className="text-sm text-cs-muted">{form.role}</p>
            </div>
          </div>

          <div className="space-y-5 p-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-cs-dark">
                Cambiar foto
              </label>
              <label className="flex cursor-pointer items-center justify-center rounded-2xl border border-dashed border-cs-border bg-cs-bg-alt px-4 py-5 text-center transition hover:border-cs-primary hover:bg-cs-primary-soft/40">
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                <span className="text-sm text-cs-muted">
                  Subir imagen JPG, PNG o WebP
                </span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-cs-border bg-cs-bg-alt p-4">
                <p className="text-xs uppercase tracking-wide text-cs-muted">Módulos</p>
                <p className="mt-2 text-2xl font-semibold text-cs-dark">{modulesCount}</p>
              </div>
              <div className="rounded-2xl border border-cs-border bg-cs-bg-alt p-4">
                <p className="text-xs uppercase tracking-wide text-cs-muted">Estado</p>
                <p className="mt-2 text-sm font-semibold text-emerald-600">Sesión activa</p>
              </div>
            </div>

            <div className="rounded-2xl border border-cs-border bg-cs-surface-alt p-4">
              <p className="text-xs uppercase tracking-wide text-cs-muted">Resumen</p>
              <p className="mt-2 text-sm leading-6 text-cs-dark">{form.bio}</p>
            </div>

            <div className="rounded-2xl border border-cs-border p-4">
              <p className="text-sm font-semibold text-cs-dark">Accesos rápidos</p>
              <div className="mt-3 space-y-3 text-sm text-cs-muted">
                <div className="flex items-center justify-between gap-3">
                  <span>Correo principal</span>
                  <span className="truncate text-cs-dark">{form.email || "-"}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Teléfono</span>
                  <span className="text-cs-dark">{form.phone || "-"}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Empresa</span>
                  <span className="text-cs-dark">{form.company_name || "-"}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <div className="rounded-3xl border border-cs-border bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-cs-dark">Información general</h3>
            <p className="mt-1 text-sm text-cs-muted">
              Campos básicos para una futura integración con backend.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <CustomInput
                label="Nombre"
                name="first_name"
                type="text"
                value={form.first_name}
                handleChange={handleChange}
              />
              <CustomInput
                label="Apellido"
                name="last_name"
                type="text"
                value={form.last_name}
                handleChange={handleChange}
              />
              <CustomInput
                label="Correo electrónico"
                name="email"
                type="email"
                value={form.email}
                handleChange={handleChange}
              />
              <CustomInput
                label="Teléfono"
                name="phone"
                type="text"
                value={form.phone}
                handleChange={handleChange}
              />
              <CustomInput
                label="Rol"
                name="role"
                type="text"
                value={form.role}
                handleChange={handleChange}
              />
              <CustomInput
                label="Empresa"
                name="company_name"
                type="text"
                value={form.company_name}
                handleChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-cs-border bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-cs-dark">Permisos visibles</h3>
              <div className="mt-4 space-y-3">
                {(user.models || []).slice(0, 6).map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className="flex items-center justify-between rounded-2xl border border-cs-border bg-cs-bg-alt px-4 py-3"
                  >
                    <div>
                      <p className="font-medium text-cs-dark">{item.name}</p>
                      <p className="text-xs text-cs-muted">Ruta: {item.link}</p>
                    </div>
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Activo
                    </span>
                  </div>
                ))}

                {modulesCount === 0 && (
                  <div className="rounded-2xl border border-dashed border-cs-border px-4 py-5 text-sm text-cs-muted">
                    No hay módulos asignados para mostrar.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-cs-border bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-cs-dark">Notas del perfil</h3>
              <p className="mt-1 text-sm text-cs-muted">
                Espacio simple para contexto del usuario mientras se define la integración real.
              </p>
              <textarea
                name="bio"
                value={form.bio}
                onChange={handleChange}
                rows={9}
                className="mt-4 w-full rounded-2xl border border-cs-border bg-cs-surface px-4 py-3 text-sm text-cs-dark shadow-sm transition focus:border-cs-primary focus:outline-none focus:ring-2 focus:ring-cs-primary/20"
                placeholder="Describe responsabilidades, área o notas rápidas del usuario"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Profile;
